import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowPathIcon,
  StarIcon,
  SparklesIcon,
  XMarkIcon,
  ClipboardDocumentListIcon,
} from "@heroicons/react/24/outline";
import {
  ChatBubbleLeftRightIcon,
  PaperAirplaneIcon,
} from "@heroicons/react/24/solid";
import { ReviewCardProps } from "../types";
import { API_CONFIG, getApiUrl } from "../config/api";
import { useAuth } from "../contexts/AuthContext";
import { useAuthenticatedFetch } from "../hooks/useAuthenticatedFetch";

const ReviewCard: React.FC<ReviewCardProps> = ({
  review,
  isNew,
  generatingResponses = {},
  analyzingReviews = {},
  onRegenerateResponse,
  onAnalyzeReview,
  showProductLink = true,
  showAnalysis = true,
  autoGeneration = false,
}) => {
  const { user } = useAuth();
  const authenticatedFetch = useAuthenticatedFetch();
  const [isReplyOpen, setIsReplyOpen] = useState(false);
  const [replyText, setReplyText] = useState("");
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [publishError, setPublishError] = useState<string | null>(null);
  const [analyzeError, setAnalyzeError] = useState<string | null>(null);
  const [showPromptNote, setShowPromptNote] = useState(false);
  const [promptNote, setPromptNote] = useState("");
  const [showReplaceTooltip, setShowReplaceTooltip] = useState(false);
  const [sendCountdown, setSendCountdown] = useState<number | null>(null);
  const sendIntervalRef = useRef<number | null>(null);
  const hasSentRef = useRef(false);
  const isCountdownRunningRef = useRef(false);

  // Templates picker state
  type TemplateItem = {
    id: string;
    title: string;
    content: string;
    category?: string | null;
  };
  const [showTemplatesPicker, setShowTemplatesPicker] = useState(false);
  const [templates, setTemplates] = useState<TemplateItem[]>([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [templatesError, setTemplatesError] = useState<string | null>(null);
  const [templatesPage, setTemplatesPage] = useState(1);
  const [templatesTotalPages, setTemplatesTotalPages] = useState(1);
  const [templatesSearch, setTemplatesSearch] = useState("");
  const templatesDebounceRef = useRef<number | null>(null);
  const pickerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (!showTemplatesPicker) return;
      if (pickerRef.current && !pickerRef.current.contains(e.target as Node)) {
        setShowTemplatesPicker(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [showTemplatesPicker]);

  const fetchTemplates = async (page: number, search: string) => {
    try {
      setTemplatesLoading(true);
      setTemplatesError(null);
      const url = new URL(getApiUrl(API_CONFIG.ENDPOINTS.TEMPLATES));
      url.searchParams.set("page", String(page));
      url.searchParams.set("limit", "10");
      if (search.trim()) url.searchParams.set("search", search.trim());
      const resp = await authenticatedFetch(url.toString(), {
        headers: { "Content-Type": "application/json" },
      });
      if (!resp.ok) {
        const data = await resp.json().catch(() => ({}));
        throw new Error(data?.message || "Не удалось загрузить шаблоны");
      }
      const data = await resp.json();
      setTemplates(data.data || []);
      setTemplatesTotalPages(data.totalPages || 1);
      setTemplatesPage(data.page || page);
    } catch (e) {
      setTemplatesError(
        e instanceof Error ? e.message : "Ошибка при загрузке шаблонов"
      );
    } finally {
      setTemplatesLoading(false);
    }
  };

  useEffect(() => {
    return () => {
      if (sendIntervalRef.current) {
        window.clearInterval(sendIntervalRef.current);
        sendIntervalRef.current = null;
      }
    };
  }, []);

  const generatePreview = async () => {
    try {
      setPreviewLoading(true);
      setPreviewError(null);
      // Убираем предварительную проверку статуса AI — проверяем по факту ответа

      const token = localStorage.getItem("token");
      const response = await fetch(
        getApiUrl(API_CONFIG.ENDPOINTS.EXTERNAL_REVIEW_AI_RESPONSE_PREVIEW),
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            platform: review.platform as any,
            externalReviewId: review.id,
            requirements:
              "Ответ должен быть от имени продавца, а не производителя",
            commentOnGeneration:
              promptNote && promptNote.trim()
                ? promptNote.trim()
                : (user as any)?.globalPromptNote &&
                    String((user as any).globalPromptNote).trim()
                  ? String((user as any).globalPromptNote).trim()
                  : undefined,
            storeName:
              user?.companyName && user.companyName.trim()
                ? user.companyName.trim()
                : review.storeName || undefined,
          }),
        }
      );
      if (!response.ok) {
        // Если проблема с подключением — показываем уведомление
        setPreviewError(
          response.status === 503
            ? "Нет связи с AI ассистентом"
            : "Ошибка генерации превью ответа"
        );
        setTimeout(() => setPreviewError(null), 2500);
      } else {
        const data = await response.json();
        setReplyText(data.responseTemplate || "");
        // Если до превью анализ отсутствовал — подтянем сохранённый анализ из БД без перезапуска анализа
        if (!review.isAnalyzed) {
          try {
            const token2 = localStorage.getItem("token");
            const analysisResp = await fetch(
              getApiUrl(
                `${API_CONFIG.ENDPOINTS.EXTERNAL_REVIEW_AI_ANALYSIS}?platform=${review.platform}&externalReviewId=${review.id}`
              ),
              {
                method: "GET",
                headers: {
                  "Content-Type": "application/json",
                  ...(token2 && { Authorization: `Bearer ${token2}` }),
                },
              }
            );
            if (analysisResp.ok) {
              const ai = await analysisResp.json();
              // Локально обновим карточку (без глобального стейта)
              // В идеале это делать через setReviews наверху, но для быстрого UX обновим локально
              (review as any).sentiment = ai.sentiment ?? null;
              (review as any).sentimentAnalysis = ai.sentimentAnalysis ?? null;
              (review as any).problems = ai.problems ?? null;
              (review as any).recommendations = ai.recommendations ?? null;
              (review as any).responseTemplate =
                ai.responseTemplate ?? review.responseTemplate;
              (review as any).isAnalyzed = ai.isAnalyzed ?? false;
              (review as any).isResponded =
                ai.isResponded ?? review.isResponded;
              (review as any).isFallback = ai.isFallback ?? false;
            }
          } catch (_) {
            // ignore refresh errors
          }
        }
      }
    } catch (e) {
      setPreviewError("Нет связи с AI ассистентом");
      setTimeout(() => setPreviewError(null), 2500);
    } finally {
      setPreviewLoading(false);
    }
  };

  const startSendCountdown = () => {
    if (!replyText.trim()) return;
    if (isCountdownRunningRef.current) return;
    if (sendIntervalRef.current) window.clearInterval(sendIntervalRef.current);
    hasSentRef.current = false;
    isCountdownRunningRef.current = true;
    setSendCountdown(3);
    sendIntervalRef.current = window.setInterval(() => {
      setSendCountdown((prev) => {
        if (prev === null) return null;
        if (prev <= 1) {
          if (sendIntervalRef.current)
            window.clearInterval(sendIntervalRef.current);
          sendIntervalRef.current = null;
          if (!hasSentRef.current) {
            hasSentRef.current = true;
            void (async () => {
              try {
                const token = localStorage.getItem("token");
                const resp = await fetch(
                  getApiUrl(
                    API_CONFIG.ENDPOINTS.PUBLISH_EXTERNAL_REVIEW_RESPONSE
                  ),
                  {
                    method: "POST",
                    headers: {
                      "Content-Type": "application/json",
                      ...(token ? { Authorization: `Bearer ${token}` } : {}),
                    },
                    body: JSON.stringify({
                      platform: review.platform,
                      externalReviewId: review.id,
                      text: replyText.trim(),
                      storeId: (review as any)?.storeId || undefined,
                    }),
                  }
                );
                if (resp?.ok) {
                  try {
                    const updated = await resp.json();
                    // Ошибка публикации на Wildberries через PATCH (прокидываем с бэка)
                    const wbPublishError = (updated as any)?.wbPublishError;
                    if (wbPublishError) {
                      const errTitle = String(wbPublishError?.title || "");
                      const errDetail = String(wbPublishError?.detail || "");
                      const errStatusText = String(
                        wbPublishError?.statusText || ""
                      );
                      const msg =
                        (errTitle || errDetail || errStatusText)
                          .toString()
                          .trim() || "Ошибка публикации ответа";
                      setPublishError(msg);
                      setTimeout(() => setPublishError(null), 4000);
                      return; // не помечаем как отвеченный
                    }
                    const newSellerAnswer =
                      (updated && (updated as any).sellerAnswer) ||
                      (updated && (updated as any).answer) ||
                      replyText.trim();
                    (review as any).sellerAnswer = newSellerAnswer || null;
                    (review as any).isAnsweredBySeller = Boolean(
                      newSellerAnswer && newSellerAnswer.length > 0
                    );
                    (review as any).isResponded = true;
                  } catch (_) {
                    (review as any).sellerAnswer = replyText.trim();
                    (review as any).isAnsweredBySeller = Boolean(
                      (review as any).sellerAnswer
                    );
                    (review as any).isResponded = true;
                  }
                  setIsReplyOpen(false);
                }
              } catch (_) {
                // ignore network errors for now; UI can be enhanced to show toast
              }
            })();
          }
          isCountdownRunningRef.current = false;
          return null;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const cancelSendCountdown = () => {
    if (sendIntervalRef.current) window.clearInterval(sendIntervalRef.current);
    sendIntervalRef.current = null;
    isCountdownRunningRef.current = false;
    hasSentRef.current = false;
    setSendCountdown(null);
  };
  const getSentimentColor = (sentiment?: string) => {
    switch (sentiment) {
      case "positive":
        return "text-emerald-700 bg-gradient-to-r from-emerald-50 to-green-50 border-emerald-200";
      case "negative":
        return "text-red-700 bg-gradient-to-r from-red-50 to-rose-50 border-red-200";
      case "neutral":
        return "text-slate-700 bg-gradient-to-r from-slate-50 to-gray-50 border-slate-200";
      default:
        return "text-slate-700 bg-gradient-to-r from-slate-50 to-gray-50 border-slate-200";
    }
  };

  const getSentimentText = (sentiment?: string) => {
    switch (sentiment) {
      case "positive":
        return "Положительный";
      case "negative":
        return "Отрицательный";
      case "neutral":
        return "Нейтральный";
      default:
        return "Не определен";
    }
  };

  const getRatingStars = (rating: number) => {
    return Array.from({ length: 5 }, (_, i) => (
      <StarIcon
        key={i}
        className={`w-4 h-4 ${
          i < rating ? "text-amber-400 fill-current" : "text-gray-300"
        }`}
      />
    ));
  };

  const getPlatformBorderGradient = (platform?: string) => {
    const p = (platform || "").toLowerCase();
    if (p === "ozon") return "from-blue-600 to-blue-700";
    if (p === "wildberries") return "from-purple-500 to-fuchsia-600";
    if (p === "yandex") return "from-yellow-400 to-amber-400";
    return "from-gray-300 to-gray-400";
  };

  const getPlatformTopBarGradient = (platform?: string) => {
    const p = (platform || "").toLowerCase();
    switch (p) {
      case "wildberries":
        return "bg-gradient-to-r from-purple-500 to-fuchsia-600";
      case "ozon":
        return "bg-gradient-to-r from-blue-600 to-blue-700";
      case "yandex":
        return "bg-gradient-to-r from-yellow-400 to-amber-400";
      default:
        return "bg-gradient-to-r from-gray-300 to-gray-400";
    }
  };

  const getPlatformBadgeClasses = (platform?: string) => {
    const p = (platform || "").toLowerCase();
    if (p === "ozon") return "bg-blue-100 text-blue-700 border-blue-200";
    if (p === "wildberries")
      return "bg-fuchsia-100 text-fuchsia-700 border-fuchsia-200";
    return "bg-yellow-100 text-yellow-700 border-yellow-200"; // yandex
  };

  // removed unused getNewReviewBackgroundColor

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const pad = (n: number) => n.toString().padStart(2, "0");
    return `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
  };

  // Картинки отключены для страницы отзывов

  // Флаг текста: для Yandex учитываем advantages/disadvantages/comment
  const hasAnyText = (() => {
    if ((review.platform || "").toLowerCase() === "yandex") {
      const ya: any = (review as any).yandex || {};
      const adv =
        typeof ya.advantages === "string" && ya.advantages.trim().length > 0;
      const dis =
        typeof ya.disadvantages === "string" &&
        ya.disadvantages.trim().length > 0;
      const com =
        typeof ya.comment === "string" && ya.comment.trim().length > 0;
      return adv || dis || com;
    }
    return Boolean(review.text && review.text.trim().length > 0);
  })();

  return (
    <div
      className={`group relative bg-white rounded-3xl shadow-lg hover:shadow-xl transition-shadow transition-colors duration-300 ease-out overflow-hidden border ${
        review.sentiment === "negative"
          ? "border-red-100 hover:border-red-200"
          : review.sentiment === "positive"
            ? "border-emerald-100 hover:border-emerald-200"
            : "border-gray-100 hover:border-gray-200"
      } ${isNew ? "new-review-accordion" : ""}`}
    >
      {/* Пульсирующий фон для новых отзывов */}
      {isNew && (
        <div className="absolute top-3 right-3 z-10">
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider rounded-full bg-white/90 text-amber-600 shadow-sm ring-1 ring-amber-300/60">
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-amber-500"></span>
            Новый
          </span>
        </div>
      )}

      {/* Градиентная полоса сверху по цвету платформы */}
      <div
        className={`absolute top-0 left-0 right-0 h-1 ${getPlatformTopBarGradient(
          review.platform
        )}`}
      ></div>

      {/* Контент отзыва */}
      <div className="relative z-10 p-4 sm:p-5">
        <div className="mb-4 w-full">
          <div className="flex flex-col space-y-2">
            {showProductLink ? (
              <div className="flex space-x-3">
                {/* Изображение товара скрыто */}
                <div className="space-y-2 min-w-0 flex-1 w-full">
                  <div className="flex items-center space-x-2">
                    <span
                      className={`text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full border ${getPlatformBadgeClasses(
                        (review as any)?.platform
                      )}`}
                    >
                      {review.platform}
                    </span>
                    {review.storeName ? (
                      (review as any)?.storeId ? (
                        <Link
                          to={`/stores/${(review as any).storeId}`}
                          className="inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-medium bg-gray-100 text-gray-700 border border-gray-200 hover:bg-gray-200"
                          title={review.storeName}
                        >
                          {review.storeName}
                        </Link>
                      ) : (
                        <span
                          className="inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-medium bg-gray-100 text-gray-700 border border-gray-200"
                          title={review.storeName}
                        >
                          {review.storeName}
                        </span>
                      )
                    ) : null}
                    {/* Имя автора для Wildberries */}
                    {String(review.platform).toLowerCase() === "wildberries" &&
                    (review as any).userName ? (
                      <span
                        className="inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-medium bg-purple-50 text-purple-700 border border-purple-200"
                        title={(review as any).userName}
                      >
                        {(review as any).userName}
                      </span>
                    ) : null}
                    {/* Имя автора для Yandex */}
                    {String(review.platform).toLowerCase() === "yandex" &&
                      (() => {
                        const ya: any = (review as any).yandex || {};
                        const author: string =
                          (review as any).author ||
                          ya.author ||
                          ya.authorName ||
                          ya.userName ||
                          "";
                        return author ? (
                          <span
                            className="inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-medium bg-yellow-50 text-yellow-700 border border-yellow-200"
                            title={author}
                          >
                            {author}
                          </span>
                        ) : null;
                      })()}
                  </div>
                  {(() => {
                    // Строго используем идентификатор товара, а не offer_id
                    // Для OZON это product_info.id; в маппинге он уже проставлен в review.product.id
                    const productId = review.product?.id;
                    const storeId = (review as any)?.storeId;
                    const to =
                      storeId && productId
                        ? `/products/${storeId}/${productId}`
                        : undefined;
                    const name =
                      (review as any).product_info?.name ||
                      review.product?.name ||
                      "";
                    return to ? (
                      <Link
                        to={to}
                        className="block w-full font-semibold text-gray-900 hover:text-blue-600 transition-colors text-base decoration-2"
                        title={name}
                      >
                        <span className="block w-full truncate">{name}</span>
                      </Link>
                    ) : (
                      <span
                        className="block max-w-full font-semibold text-gray-900 text-base truncate"
                        title={name}
                      >
                        {name}
                      </span>
                    );
                  })()}
                </div>
              </div>
            ) : (
              <div className="flex items-center space-x-3 min-w-0 w-full">
                {/* Изображение товара скрыто */}
                <div className="flex items-center space-x-2 min-w-0 flex-1">
                  <span
                    className={`text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full border ${getPlatformBadgeClasses(
                      review.platform
                    )}`}
                  >
                    {review.platform}
                  </span>
                  {review.storeName ? (
                    (review as any)?.storeId ? (
                      <Link
                        to={`/stores/${(review as any).storeId}`}
                        className="inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-medium bg-gray-100 text-gray-700 border border-gray-200 hover:bg-gray-200"
                        title={review.storeName}
                      >
                        {review.storeName}
                      </Link>
                    ) : (
                      <span
                        className="inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-medium bg-gray-100 text-gray-700 border border-gray-200"
                        title={review.storeName}
                      >
                        {review.storeName}
                      </span>
                    )
                  ) : null}
                  <span
                    className="block max-w-full font-semibold text-gray-900 text-base truncate"
                    title={review.product?.name || ""}
                  >
                    {review.product?.name || ""}
                  </span>
                </div>
              </div>
            )}
          </div>
          <div className="flex items-center space-x-3">
            {/* sentiment moved next to rating */}
          </div>
        </div>

        {/* Контрастный блок с текстом комментария в современном стиле */}
        <div
          className={`relative mb-4 rounded-2xl p-[1px] bg-gradient-to-r ${getPlatformBorderGradient(review.platform)}`}
        >
          <div className="relative rounded-2xl overflow-hidden bg-white">
            <div className="flex items-center justify-between px-4 pt-3 pb-2 border-b border-gray-100">
              <div className="flex items-center space-x-3">
                <div className="w-9 h-9 rounded-xl flex items-center justify-center text-gray-700 shadow-sm ring-1 ring-gray-200 bg-slate-50">
                  <ChatBubbleLeftRightIcon className="w-5 h-5" />
                </div>
                <div className="flex items-center space-x-1">
                  {getRatingStars(review.rating)}
                </div>
              </div>
              {review.sentiment && (
                <span
                  className={`px-2.5 py-1 rounded-full text-xs font-semibold border ${getSentimentColor(review.sentiment)} shadow-sm`}
                >
                  {getSentimentText(review.sentiment)}
                </span>
              )}
            </div>

            <div className="px-4 py-3 space-y-2">
              {review.platform === "yandex" && (review as any).yandex ? (
                (() => {
                  const ya: any = (review as any).yandex || {};
                  const hasAdv =
                    typeof ya.advantages === "string" &&
                    ya.advantages.trim().length > 0;
                  const hasDis =
                    typeof ya.disadvantages === "string" &&
                    ya.disadvantages.trim().length > 0;
                  const hasCom =
                    typeof ya.comment === "string" &&
                    ya.comment.trim().length > 0;
                  const onlyComment = hasCom && !hasAdv && !hasDis;
                  if (!hasAdv && !hasDis && !hasCom) {
                    return (
                      <p className="text-gray-800 leading-relaxed text-[15px]">
                        {review.text}
                      </p>
                    );
                  }
                  return (
                    <div className="space-y-1.5">
                      {hasAdv ? (
                        <div className="text-[15px] text-gray-800 whitespace-pre-line">
                          <span className="text-xs text-gray-500 mr-2">
                            Преимущество
                          </span>
                          {ya.advantages}
                        </div>
                      ) : null}
                      {hasDis ? (
                        <div className="text-[15px] text-gray-800 whitespace-pre-line">
                          <span className="text-xs text-gray-500 mr-2">
                            Недостатки
                          </span>
                          {ya.disadvantages}
                        </div>
                      ) : null}
                      {hasCom ? (
                        onlyComment ? (
                          <div className="text-[15px] text-gray-800 whitespace-pre-line">
                            {ya.comment}
                          </div>
                        ) : (
                          <div className="text-[15px] text-gray-800 whitespace-pre-line">
                            <span className="text-xs text-gray-500 mr-2">
                              Комментарий
                            </span>
                            {ya.comment}
                          </div>
                        )
                      ) : null}
                    </div>
                  );
                })()
              ) : (
                <p className="text-gray-800 leading-relaxed text-[15px]">
                  {review.text}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Анализ отзыва */}
        {showAnalysis &&
          (review.isAnalyzed ? (
            <div className="bg-gradient-to-br from-slate-50 to-gray-50 rounded-xl p-4 mb-4 border border-slate-200 shadow-sm">
              <h4 className="font-bold text-gray-900 mb-4 flex items-center">
                <div className="w-2 h-2 bg-blue-500 rounded-full mr-3"></div>
                Анализ отзыва
              </h4>
              {review.sentimentAnalysis && (
                <div className="mb-4">
                  <div className="text-sm font-semibold text-gray-700 mb-2 flex items-center">
                    <div className="w-1.5 h-1.5 bg-blue-400 rounded-full mr-2"></div>
                    Анализ:
                  </div>
                  <p className="text-sm text-gray-600 bg-white p-2 rounded-lg border border-gray-100">
                    {review.sentimentAnalysis}
                  </p>
                </div>
              )}
              {review.problems && (
                <div className="mb-4">
                  <div className="text-sm font-semibold text-gray-700 mb-2 flex items-center">
                    <div className="w-1.5 h-1.5 bg-orange-400 rounded-full mr-2"></div>
                    Проблемы:
                  </div>
                  <p className="text-sm text-gray-600 bg-white p-2 rounded-lg border border-gray-100">
                    {review.problems}
                  </p>
                </div>
              )}
              {review.recommendations && (
                <div className="mb-4">
                  <div className="text-sm font-semibold text-gray-700 mb-2 flex items-center">
                    <div className="w-1.5 h-1.5 bg-green-400 rounded-full mr-2"></div>
                    Рекомендации:
                  </div>
                  <p className="text-sm text-gray-600 bg-white p-2 rounded-lg border border-gray-100">
                    {review.recommendations}
                  </p>
                </div>
              )}
            </div>
          ) : null)}

        {/* Блок с ответом продавца */}
        {review.isAnsweredBySeller && (
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-4 mb-4 border-2 border-blue-200 shadow-sm">
            <div className="flex items-center space-x-3 mb-3">
              <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
              <span className="text-sm font-bold text-blue-700">
                Ответ продавца
              </span>
            </div>
            <p className="text-sm text-blue-600 leading-relaxed bg-white p-2 rounded-lg border border-blue-100">
              {review.sellerAnswer}
            </p>
          </div>
        )}

        {/* Блок с ответами */}
        {review.responseTemplate && (
          <div className="bg-gradient-to-br from-emerald-50 to-green-50 rounded-xl p-4 mb-4 border-2 border-emerald-200 shadow-sm">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center space-x-3">
                <div className="w-3 h-3 bg-emerald-500 rounded-full"></div>
                <span className="text-sm font-bold text-emerald-700">
                  Рекомендуемый ответ
                </span>
                <button
                  onClick={() =>
                    onRegenerateResponse?.(review.id, review.platform as any)
                  }
                  disabled={generatingResponses[review.id]}
                  className="flex items-center justify-center w-7 h-7 rounded-full bg-emerald-100 text-emerald-600 hover:bg-emerald-200 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 group shadow-sm border border-emerald-200"
                  title="Перегенерировать ответ"
                >
                  <ArrowPathIcon
                    className={`w-4 h-4 ${generatingResponses[review.id] ? "animate-spin" : ""}`}
                  />
                </button>
              </div>
            </div>
            <p className="text-sm text-emerald-600 leading-relaxed bg-white p-2 rounded-lg border border-emerald-100">
              {review.responseTemplate}
            </p>
          </div>
        )}

        {/* Поле для ручного ответа */}
        {isReplyOpen && (
          <div className="bg-gradient-to-br from-slate-50 to-gray-50 rounded-xl p-4 mb-4 border border-slate-200 shadow-sm">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center space-x-3">
                <div className="w-3 h-3 bg-indigo-500 rounded-full"></div>
                <span className="text-sm font-bold text-gray-800">
                  Ваш ответ
                </span>
              </div>
              <div className="flex items-center">
                {/* Кнопка выбора шаблонов — слева от ИИ-кнопок */}
                <div className="relative mr-4" ref={pickerRef}>
                  <button
                    onClick={async () => {
                      const willOpen = !showTemplatesPicker;
                      setShowTemplatesPicker(willOpen);
                      if (willOpen) {
                        await fetchTemplates(1, templatesSearch);
                      }
                    }}
                    title="Выбрать шаблон"
                    className="peer flex items-center justify-center w-9 h-9 rounded-full border transition-all duration-200 bg-gradient-to-r from-amber-50 to-orange-50 text-amber-700 border-amber-200 hover:from-amber-100 hover:to-orange-100 hover:border-amber-300 shadow-sm"
                  >
                    <ClipboardDocumentListIcon className="w-4 h-4" />
                  </button>
                  {/* Tooltip */}
                  <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 z-20">
                    <span className="pointer-events-none whitespace-nowrap text-[11px] text-white bg-gray-900/90 border border-gray-800 rounded px-2 py-1 opacity-0 peer-hover:opacity-100 transition-opacity duration-150 shadow-lg">
                      Выбрать шаблон
                    </span>
                  </div>
                  {showTemplatesPicker && (
                    <div
                      className="absolute right-0 mt-2 w-[420px] max-w-[90vw] bg-white border rounded-xl shadow-xl ring-1 ring-black/5 p-3 z-30"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <input
                          type="text"
                          value={templatesSearch}
                          onChange={(e) => {
                            const v = e.target.value;
                            setTemplatesSearch(v);
                            if (templatesDebounceRef.current)
                              window.clearTimeout(templatesDebounceRef.current);
                            templatesDebounceRef.current = window.setTimeout(
                              () => fetchTemplates(1, v),
                              300
                            );
                          }}
                          placeholder="Поиск шаблонов..."
                          className="flex-1 input"
                        />
                        <button
                          onClick={async () =>
                            fetchTemplates(1, templatesSearch)
                          }
                          className="btn-secondary"
                        >
                          Найти
                        </button>
                      </div>
                      <div className="max-h-72 overflow-auto divide-y border rounded-md">
                        {templatesLoading ? (
                          <div className="p-3 text-sm text-gray-500">
                            Загрузка...
                          </div>
                        ) : templatesError ? (
                          <div className="p-3 text-sm text-rose-600">
                            {templatesError}
                          </div>
                        ) : templates.length === 0 ? (
                          <div className="p-3 text-sm text-gray-500">
                            Шаблоны не найдены
                          </div>
                        ) : (
                          templates.map((t) => (
                            <button
                              key={t.id}
                              onClick={() => {
                                setReplyText(t.content || "");
                                setShowTemplatesPicker(false);
                              }}
                              className="w-full text-left p-3 hover:bg-gray-50 focus:bg-gray-50"
                            >
                              <div className="flex items-center gap-2">
                                <span className="text-sm font-semibold text-gray-800 truncate">
                                  {t.title}
                                </span>
                                {t.category ? (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-gray-100 text-gray-700 border">
                                    {t.category}
                                  </span>
                                ) : null}
                              </div>
                              <div className="mt-1 text-xs text-gray-600 line-clamp-2">
                                {t.content}
                              </div>
                            </button>
                          ))
                        )}
                      </div>
                      <div className="flex items-center justify-between mt-2 text-xs text-gray-600">
                        <button
                          className="btn-secondary"
                          onClick={() =>
                            fetchTemplates(
                              Math.max(1, templatesPage - 1),
                              templatesSearch
                            )
                          }
                          disabled={templatesPage <= 1 || templatesLoading}
                        >
                          Назад
                        </button>
                        <span>
                          Стр. {templatesPage} из {templatesTotalPages}
                        </span>
                        <button
                          className="btn-secondary"
                          onClick={() =>
                            fetchTemplates(
                              Math.min(templatesTotalPages, templatesPage + 1),
                              templatesSearch
                            )
                          }
                          disabled={
                            templatesPage >= templatesTotalPages ||
                            templatesLoading
                          }
                        >
                          Далее
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Группа: ИИ генерация + комментарий (иконки) */}
                <div className="flex items-center gap-1">
                  <div className="relative">
                    {previewError && (
                      <span className="absolute right-full mr-2 top-1/2 -translate-y-1/2 text-[11px] text-rose-600 bg-rose-50 border border-rose-200 rounded-full px-2 py-0.5 whitespace-nowrap">
                        {previewError}
                      </span>
                    )}
                    {showReplaceTooltip && (
                      <div className="absolute right-full mr-2 top-1/2 -translate-y-1/2 z-20 w-64 p-3 rounded-lg border border-gray-200 bg-white shadow-lg">
                        <div className="text-xs text-gray-700 mb-2">
                          Введенный вами текст в поле будет заменен
                          сгенерированным текстом с помощью ИИ
                        </div>
                        <div className="flex items-center justify-end space-x-2">
                          <button
                            onClick={() => setShowReplaceTooltip(false)}
                            className="px-2 py-1 text-[11px] rounded-md border border-gray-200 text-gray-600 hover:bg-gray-50"
                          >
                            Отмена
                          </button>
                          <button
                            onClick={async () => {
                              setShowReplaceTooltip(false);
                              if (!previewLoading && hasAnyText) {
                                await generatePreview();
                              }
                            }}
                            className="px-2 py-1 text-[11px] rounded-md border border-indigo-200 text-indigo-700 bg-indigo-50 hover:bg-indigo-100"
                          >
                            Заменить
                          </button>
                        </div>
                      </div>
                    )}
                    <button
                      onClick={async () => {
                        if (previewLoading || !hasAnyText) return;
                        if (replyText.trim().length > 0) {
                          setShowReplaceTooltip(true);
                          return;
                        }
                        await generatePreview();
                      }}
                      title={
                        hasAnyText
                          ? "Сгенерировать ответ с помощью ИИ"
                          : "Недоступно для отзывов без текста"
                      }
                      className={`peer flex items-center justify-center w-9 h-9 rounded-full border transition-all duration-200 ${
                        previewLoading || !hasAnyText
                          ? "bg-indigo-100 text-indigo-400 border-indigo-200 cursor-not-allowed"
                          : "bg-gradient-to-r from-indigo-50 to-purple-50 text-indigo-700 border-indigo-200 hover:from-indigo-100 hover:to-purple-100 hover:border-indigo-300 shadow-sm"
                      }`}
                      disabled={previewLoading || !hasAnyText}
                    >
                      <SparklesIcon
                        className={`w-4 h-4 ${previewLoading ? "animate-pulse" : ""}`}
                      />
                    </button>
                    {/* Tooltip: Сгенерировать */}
                    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 z-20">
                      <span className="pointer-events-none whitespace-nowrap text-[11px] text-white bg-gray-900/90 border border-gray-800 rounded px-2 py-1 opacity-0 peer-hover:opacity-100 transition-opacity duration-150 shadow-lg">
                        {hasAnyText
                          ? "Сгенерировать ответ с помощью ИИ"
                          : "Недоступно для отзывов без текста"}
                      </span>
                    </div>
                  </div>
                  <div className="relative">
                    <button
                      onClick={() => {
                        if (!hasAnyText) return;
                        setShowPromptNote((v) => !v);
                      }}
                      className={`peer flex items-center justify-center w-9 h-9 rounded-full border transition-all duration-200 ${
                        !hasAnyText
                          ? "bg-blue-100 text-blue-300 border-blue-200 cursor-not-allowed"
                          : "bg-gradient-to-r from-blue-50 to-cyan-50 text-blue-700 border-blue-200 hover:from-blue-100 hover:to-cyan-100 hover:border-blue-300 shadow-sm"
                      }`}
                      title={
                        hasAnyText
                          ? "Добавить комментарий к промпту"
                          : "Недоступно для отзывов без текста"
                      }
                      aria-label={
                        hasAnyText
                          ? "Добавить комментарий к промпту"
                          : "Недоступно для отзывов без текста"
                      }
                      disabled={!hasAnyText}
                    >
                      <ChatBubbleLeftRightIcon className="w-4 h-4" />
                    </button>
                    {/* Tooltip: Комментарий */}
                    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 z-20">
                      <span className="pointer-events-none whitespace-nowrap text-[11px] text-white bg-gray-900/90 border border-gray-800 rounded px-2 py-1 opacity-0 peer-hover:opacity-100 transition-opacity duration-150 shadow-lg">
                        {hasAnyText
                          ? "Добавить комментарий к ИИ"
                          : "Недоступно для отзывов без текста"}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Первичное действие: Отправить */}
                {sendCountdown === null ? (
                  <button
                    onClick={() => startSendCountdown()}
                    disabled={!replyText.trim()}
                    className="flex items-center gap-2 h-9 px-4 rounded-full text-xs font-semibold border-2 bg-gradient-to-r from-emerald-50 to-green-50 text-emerald-700 border-emerald-200 hover:from-emerald-100 hover:to-green-100 hover:border-emerald-300 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed ml-4"
                  >
                    <PaperAirplaneIcon className="w-4 h-4" />
                    <span>Отправить</span>
                  </button>
                ) : (
                  <div className="flex items-center gap-2 ml-4 h-9">
                    <span className="text-xs text-gray-600">
                      Ответ будет отправлен через {sendCountdown} секун
                      {sendCountdown === 1
                        ? "ду"
                        : sendCountdown === 2 ||
                            sendCountdown === 3 ||
                            sendCountdown === 4
                          ? "ды"
                          : "д"}
                      ...
                    </span>
                    <button
                      onClick={cancelSendCountdown}
                      className="h-9 px-3 rounded-full text-xs font-semibold border-2 bg-gradient-to-r from-rose-50 to-red-50 text-rose-700 border-rose-200 hover:from-rose-100 hover:to-red-100 hover:border-rose-300 shadow-sm"
                    >
                      Отменить отправку
                    </button>
                  </div>
                )}

                {/* Закрыть блок */}
                <button
                  onClick={() => setIsReplyOpen(false)}
                  className="flex items-center justify-center w-9 h-9 rounded-full border transition-all duration-200 text-xs font-semibold bg-gradient-to-r from-rose-50 to-red-50 text-rose-700 border-rose-200 hover:from-rose-100 hover:to-red-100 hover:border-rose-300 shadow-sm ml-4"
                  title="Свернуть блок 'Ваш ответ'"
                  aria-label="Свернуть блок 'Ваш ответ'"
                >
                  <XMarkIcon className="w-4 h-4" />
                </button>
                {/* Убрали дублирующую кнопку "Выбрать шаблон" справа */}
              </div>
            </div>
            {publishError && (
              <div className="mb-3">
                <div className="text-[12px] text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2">
                  {publishError}
                </div>
              </div>
            )}
            {showPromptNote && (
              <div className="mb-3">
                <textarea
                  value={promptNote}
                  onChange={(e) => setPromptNote(e.target.value)}
                  placeholder="Что важно учесть при генерации ответа..."
                  disabled={previewLoading || !!generatingResponses[review.id]}
                  className={`w-full h-[80px] p-3 rounded-xl border text-sm text-gray-800 shadow-sm ${
                    previewLoading || generatingResponses[review.id]
                      ? "border-gray-200 bg-gray-100 cursor-not-allowed opacity-70"
                      : "border-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-300 focus:border-blue-300"
                  }`}
                />
              </div>
            )}
            <textarea
              value={replyText}
              onChange={(e) => setReplyText(e.target.value)}
              placeholder="Введите ваш ответ..."
              className="w-full h-[200px] p-3 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-300 focus:border-indigo-300 text-sm text-gray-800 shadow-sm"
            />
          </div>
        )}

        <div className="flex items-center justify-between text-sm text-gray-500 pt-3 border-t border-gray-100">
          <div className="flex items-center space-x-3">
            <span className="font-medium">
              {formatDate(review.published_at)}
            </span>
            <span className="text-gray-300">•</span>
            <span className="text-gray-400 font-mono text-xs bg-gray-100 px-2 py-1 rounded">
              ID: {review.id}
            </span>
          </div>
          <div className="flex items-center space-x-2">
            {review.isAnalyzed && (
              <span className="px-3 py-1 bg-gradient-to-r from-gray-100 to-gray-200 text-gray-700 rounded-full text-xs font-semibold border border-gray-200">
                Проанализирован
              </span>
            )}
            {review.isResponded && (
              <span className="px-3 py-1 bg-gradient-to-r from-green-100 to-emerald-100 text-green-700 rounded-full text-xs font-semibold border border-green-200">
                Ответ дан
              </span>
            )}
            {/* Анализ скрываем для отзывов без текста; ответ теперь доступен всегда */}
            {hasAnyText && !review.isAnalyzed && onAnalyzeReview && (
              <div className="relative">
                {analyzeError && (
                  <span className="absolute right-full mr-2 top-1/2 -translate-y-1/2 text-[11px] text-rose-600 bg-rose-50 border border-rose-200 rounded-full px-2 py-0.5 whitespace-nowrap">
                    {analyzeError}
                  </span>
                )}
                <button
                  onClick={async () => {
                    setAnalyzeError(null);
                    const ok = await onAnalyzeReview?.(
                      review.id,
                      review.platform as any
                    );
                    if (ok !== true) {
                      setAnalyzeError("Нет связи с AI ассистентом");
                      setTimeout(() => setAnalyzeError(null), 2500);
                    }
                  }}
                  disabled={
                    analyzingReviews[review.id] ||
                    generatingResponses[review.id]
                  }
                  className={`flex items-center space-x-2 px-3 py-1 rounded-full border-2 transition-all duration-200 text-xs font-semibold ${
                    analyzingReviews[review.id] ||
                    generatingResponses[review.id]
                      ? "bg-blue-100 text-blue-400 border-blue-200 cursor-not-allowed"
                      : "bg-gradient-to-r from-blue-50 to-indigo-50 text-blue-600 border-blue-200 hover:from-blue-100 hover:to-indigo-100 hover:border-blue-300"
                  } disabled:opacity-50 disabled:cursor-not-allowed`}
                  title="Провести анализ отзыва"
                >
                  <ArrowPathIcon
                    className={`w-3 h-3 ${analyzingReviews[review.id] ? "animate-spin" : ""}`}
                  />
                  <span>
                    {analyzingReviews[review.id] ? "Анализируем..." : "Анализ"}
                  </span>
                </button>
              </div>
            )}
            {!review.responseTemplate &&
              (String(review.platform).toLowerCase() !== "wildberries" ||
                review.editable !== false) && (
                <>
                  <button
                    onClick={() => setIsReplyOpen((v) => !v)}
                    className="flex items-center space-x-2 px-3 py-1 rounded-full border-2 transition-all duration-200 text-xs font-semibold bg-gradient-to-r from-indigo-50 to-purple-50 text-indigo-700 border-indigo-200 hover:from-indigo-100 hover:to-purple-100 hover:border-indigo-300 shadow-sm"
                  >
                    <span>Ответить</span>
                  </button>
                  {isReplyOpen && <></>}
                </>
              )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReviewCard;
